import React, { useEffect, useState } from 'react';
import commonStyles from '../BetSlip.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import BetLines from '../BetLines/BetLines';
import SystemCombinations from '../SystemCombinations/SystemCombinations';
import Actions from '../Actions/Actions';
import StakeInputContainer from '../StakeInputContainer/StakeInputContainer';
import { BET_PLACEMENT_SUBMIT_BUTTON_TYPES, BET_SLIP_TABS, PRODUCER_TYPES } from '../../../constants/defaultValues';
import Summary from '../Summary/Summary';
import Footer from '../Footer/Footer';
import betSlipCalculator from '../../../utils/betSlipCalculator';
import {
    bookBet,
    changeBetSlipCombinations,
    changeBetSlipStakes,
    placeBet,
    printCoupon,
    validateBetSlip,
} from '../../../store/actions/betSlip';
import Validations from '../Validations/Validations';
import { generateCouponPrintDetails, isVoucherMode } from '../../../utils/helpers';
import { ticketTypeEnum } from '../../../constants/enums';

const TicketDetails = props => {
    const dispatch = useDispatch();

    const { betLines, activeTab, stakes, selectedOddChangeDecision, selectedCombinations, stakeLimits, validations } =
        useSelector(state => state.betSlip);
    const cashierInfo = useSelector(state => state.auth.cashierInfo);

    const [submitButtonType, setSubmitButtonType] = useState(BET_PLACEMENT_SUBMIT_BUTTON_TYPES.submit);

    const { resetBetSlip } = props;

    useEffect(() => {
        dispatch(
            validateBetSlip({
                activeTab,
                betLines,
                currentBalance: cashierInfo.currentBalance,
                selectedCombinations,
                selectedOddChangeDecision,
                stakeLimits,
                stakes,
                isVoucherMode: isVoucherMode(cashierInfo),
            }),
        );
    }, [
        activeTab,
        betLines,
        cashierInfo.currentBalance,
        selectedCombinations,
        selectedOddChangeDecision,
        stakeLimits,
        stakes,
    ]);

    useEffect(() => {
        if (selectedCombinations.length > 0) {
            const systems = betSlipCalculator.generateSystems(betLines, stakes[ticketTypeEnum.SYSTEM]);

            if (validations.common.length > 0 && systems.length === 0) {
                dispatch(changeBetSlipCombinations([]));
            }
        }
    }, [betLines, selectedCombinations, stakes, validations.common.length]);

    const changeBetSlipStakeValues = ({ type, stake }) => {
        dispatch(changeBetSlipStakes({ type, stake }));
    };

    const handleTicketPlacement = ({ oddChangeDecision }) => {
        const { totalOdds, totalStake, expectedBetsCount, potentialPayout } = betSlipCalculator.calculateTicketDetails({
            betLines,
            activeTab,
            stakes,
            selectedCombinations,
        });

        const data = {
            totalStake: totalStake,
            totalOdds: totalOdds,
            expectedBets: expectedBetsCount,
            ticketType: activeTab,
            acceptanceType: oddChangeDecision || selectedOddChangeDecision,
            maxPaymentAmount: potentialPayout,
            bets: betLines.map(betLine => ({
                ...betLine,
                marketName: betLine.marketTemplateName,
                marketLabel: betLine.marketLabel,
                producerType: betLine.isLive ? PRODUCER_TYPES.live : PRODUCER_TYPES.preMatch,
            })),
            systemValues: selectedCombinations.map(system => system + betSlipCalculator.getBankers(betLines).length),
        };

        dispatch(placeBet(data));
    };

    const handleBookBet = async () => {
        const { totalStake } = betSlipCalculator.calculateTicketDetails({
            betLines,
            activeTab,
            stakes,
            selectedCombinations,
        });

        const newStakes = { ...stakes };
        Object.keys(newStakes)
            .filter(key => +key !== activeTab)
            .forEach(key => (newStakes[key] = stakeLimits.sportMinimum));

        const data = {
            totalStake,
            ticketType: activeTab,
            acceptanceType: selectedOddChangeDecision,
            selectedCombinations,
            bets: betLines.map(betLine => ({
                ...betLine,
                marketName: betLine.marketTemplateName,
                stake: activeTab === BET_SLIP_TABS.single.id ? betLine.stake : stakeLimits.sportMinimum,
            })),
            stakes: newStakes,
        };

        const generatedCoupon = await dispatch(bookBet(data));

        if (generatedCoupon) {
            const couponDetails = generateCouponPrintDetails({
                couponData: generatedCoupon,
                stakes,
                cashierCode: cashierInfo.code,
            });
            await printCoupon(couponDetails);
        }
    };

    const submitTicket = async ({ oddChangeDecision } = {}) => {
        if (isVoucherMode(cashierInfo)) {
            handleTicketPlacement({ oddChangeDecision });
        } else {
            handleBookBet();
        }
    };

    return (
        <div className={commonStyles.betSlipWrap}>
            <BetLines />
            <Validations />
            {betLines.length > 0 ? (
                <>
                    <SystemCombinations />
                    <Actions resetBetSlip={resetBetSlip} />
                    <StakeInputContainer
                        show={activeTab === BET_SLIP_TABS.combo.id}
                        type={BET_SLIP_TABS.combo.id}
                        stake={stakes[BET_SLIP_TABS.combo.id]}
                        setStake={changeBetSlipStakeValues}
                        isLive={betLines.some(b => b.isLive)}
                    />
                    <StakeInputContainer
                        show={activeTab === BET_SLIP_TABS.system.id}
                        type={BET_SLIP_TABS.system.id}
                        stake={stakes[BET_SLIP_TABS.system.id]}
                        setStake={changeBetSlipStakeValues}
                        isLive={betLines.some(b => b.isLive)}
                    />
                    <Summary />
                    <Footer
                        onPlaceBetClick={submitTicket}
                        submitButtonType={submitButtonType}
                        setSubmitButtonType={setSubmitButtonType}
                    />
                </>
            ) : null}
        </div>
    );
};

export default TicketDetails;
